/**
 * Standardized Loading State Components
 * 
 * Provides consistent loading states across the application for better UX.
 * All components use Tailwind CSS for styling and are fully customizable.
 */

import React from 'react';
import { Skeleton } from './ui/skeleton';
import { Card, CardContent, CardHeader } from './ui/card';

/**
 * Data table skeleton with customizable rows and columns
 */
export const DataTableSkeleton = ({ 
  rows = 5, 
  columns = 4,
  className = ""
}: { 
  rows?: number; 
  columns?: number;
  className?: string;
}) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex gap-4">
        {Array.from({ length: columns }).map((_, j) => (
          <Skeleton key={j} className="h-12 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

/**
 * Form skeleton with customizable number of fields
 */
export const FormSkeleton = ({ 
  fields = 6,
  className = ""
}: { 
  fields?: number;
  className?: string;
}) => (
  <div className={`space-y-4 ${className}`}>
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
    ))}
  </div>
);

/**
 * Card skeleton for individual cards
 */
export const CardSkeleton = ({ className = "" }: { className?: string }) => (
  <Card className={className}>
    <CardHeader>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-64" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-32 w-full" />
    </CardContent>
  </Card>
);

/**
 * Dashboard skeleton with multiple cards and data table
 */
export const DashboardSkeleton = ({ className = "" }: { className?: string }) => (
  <div className={`space-y-6 ${className}`}>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
    <DataTableSkeleton rows={8} columns={5} />
  </div>
);

/**
 * List skeleton for simple lists
 */
export const ListSkeleton = ({ 
  items = 5,
  className = ""
}: { 
  items?: number;
  className?: string;
}) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center space-x-4">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Profile skeleton for user profiles
 */
export const ProfileSkeleton = ({ className = "" }: { className?: string }) => (
  <div className={`space-y-4 ${className}`}>
    <div className="flex items-center space-x-4">
      <Skeleton className="h-16 w-16 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  </div>
);

/**
 * Chart skeleton for data visualizations
 */
export const ChartSkeleton = ({ 
  height = 300,
  className = ""
}: { 
  height?: number;
  className?: string;
}) => (
  <div className={`space-y-4 ${className}`}>
    <div className="flex justify-between items-center">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-4 w-20" />
    </div>
    <Skeleton className={`w-full`} style={{ height: `${height}px` }} />
  </div>
);

/**
 * Navigation skeleton for sidebar navigation
 */
export const NavigationSkeleton = ({ 
  items = 6,
  className = ""
}: { 
  items?: number;
  className?: string;
}) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center space-x-3">
        <Skeleton className="h-5 w-5" />
        <Skeleton className="h-4 flex-1" />
      </div>
    ))}
  </div>
);

/**
 * Button skeleton for loading buttons
 */
export const ButtonSkeleton = ({ 
  width = "w-24",
  className = ""
}: { 
  width?: string;
  className?: string;
}) => (
  <Skeleton className={`h-10 ${width} ${className}`} />
);

/**
 * Text skeleton for loading text content
 */
export const TextSkeleton = ({ 
  lines = 3,
  className = ""
}: { 
  lines?: number;
  className?: string;
}) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`} 
      />
    ))}
  </div>
);

/**
 * Modal skeleton for loading modals
 */
export const ModalSkeleton = ({ className = "" }: { className?: string }) => (
  <div className={`space-y-4 ${className}`}>
    <div className="flex justify-between items-center">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-6 w-6" />
    </div>
    <FormSkeleton fields={4} />
    <div className="flex justify-end space-x-2">
      <ButtonSkeleton width="w-20" />
      <ButtonSkeleton width="w-24" />
    </div>
  </div>
);

/**
 * Page skeleton for full page loading
 */
export const PageSkeleton = ({ className = "" }: { className?: string }) => (
  <div className={`space-y-6 ${className}`}>
    <div className="flex justify-between items-center">
      <Skeleton className="h-8 w-48" />
      <ButtonSkeleton width="w-32" />
    </div>
    <CardSkeleton />
    <DataTableSkeleton rows={10} columns={6} />
  </div>
);